'use client';

import React from 'react';
import AgeGateComponent from '@ab-inbev-eu/ui-library/components/composite/age-gate';
import { usePathname } from 'next/navigation';
import type { GetCampaignResponse } from '@ab-inbev-eu/campaign/src/models/campaign';

import { useTranslation } from '../../../../../../../i18n/client';
import { useAnalytics } from '../../../../../../../components/analytics/hooks';

export function AgeGate({
  ageGateConfig,
  brandLogoUrl,
  onPassed,
  alwaysVisible = true,
  children,
}: React.PropsWithChildren<{
  onPassed: () => void;
  brandLogoUrl: string;
  alwaysVisible?: boolean;
  ageGateConfig?: GetCampaignResponse['template']['ageGate'];
}>) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const analytics = useAnalytics();

  const { subTitle, footerText } = ageGateConfig ?? {
    subTitle: ['age_gate.enter_dob', 'Enter your date of birth'],
    footerText: [
      'age_gate.drink_responsibly',
      'Please do not share or forward this website or its contents to anyone under 18.',
    ],
  };
  const isPublicPage = pathname.includes('/terms') || pathname.includes('/privacy-policy');

  return (
    <AgeGateComponent
      onAgeGatePassed={(userYear) => {
        analytics.track('Age Gate Enter Clicked', {
          age: { age: String(userYear) },
        });
        onPassed();
      }}
      onAgeGateBlocked={() => {
        analytics.track('Age Gate Block');
      }}
      logoProps={{
        url: brandLogoUrl,
      }}
      headerTextProps={{
        text: t('age_gate.enter_dob', 'Enter your date of birth'),
      }}
      subTitleTextProps={{
        text: t(subTitle[0], subTitle[1]),
      }}
      bottomTextProps={{
        text: t(footerText[0], footerText[1]),
      }}
      config={{
        isPublicPage: isPublicPage,
        alwaysVisible,
      }}
      layoutProps={{
        contentContainerClassName: '[&>*]:animate-pulse-fade-in',
      }}
      ageLimitProps={{
        headerTextProps: {
          text: t('age_gate.age_limit', 'Age limit.'),
        },
        subTitleTextProps: {
          text: t('age_gate.age_limit_info', 'You must be older than 18 years to use this website.'),
        },
        layoutProps: {
          contentContainerClassName: '[&>*]:animate-pulse-fade-in',
        },
      }}
      formProps={{
        labels: {
          errorOnRequired: t('this_field_is_required', 'This field is required.'),
          errorOnInvalid: t('invalid_dob', 'Invalid date of birth.'),
          dayInputTitle: t('day', 'Day'),
          monthInputTitle: t('month', 'Month'),
          yearInputTitle: t('year', 'Year'),
          submitButtonText: t('age_gate.enter', 'Enter'),
        },
        inputClasses: {
          label: 'text-neutral-100 [&]:font-label-m [&]:text-label-m',
          input:
            'bg-neutral-100/10 caret-neutral-100 text-neutral-100 font-body-m [&]:text-body-m placeholder:text-center text-center',
          root: 'border-neutral-70 focus-within:!border-neutral-100 focus-within:shadow-[inset_0px_0px_0px_1px_rgba(var(--neutral-100))]',
          container: '[&>p]:font-body-s [&>p]:text-body-s',
        },
      }}
      contentContainerClassName="animate-pulse-fade-in text-neutral-100"
    >
      {children}
    </AgeGateComponent>
  );
}
